/**
 * Pure helpers for the prospect follow-up workspace page (Lot 2).
 * Kept free of React so pagination/process races can be unit-tested.
 */

import {
  clearQuickFollowUpRefreshFailure,
  enterQuickFollowUpConflictReview,
  markQuickFollowUpConflictReloadFailure,
  markQuickFollowUpRefreshFailure,
  syncQuickFollowUpFormFromProspect,
} from './adminProspectQuickFollowUpController.js';

/**
 * After Process/Reopen removes a row from the active filter queue, decide next UI step.
 * @param {{
 *   previousItems: Array<{ id: string }>,
 *   itemId: string,
 *   nextPageToken: string | null,
 * }} args
 * @returns {{
 *   items: Array<{ id: string }>,
 *   status: 'ready' | 'empty',
 *   shouldLoadMore: boolean,
 *   pageToken: string | null,
 * }}
 */
export function planResponsesAfterLeavingQueue({
  previousItems = [],
  itemId,
  nextPageToken = null,
} = {}) {
  const items = (Array.isArray(previousItems) ? previousItems : []).filter(
    (row) => row && row.id !== itemId,
  );
  if (items.length > 0) {
    return { items, status: 'ready', shouldLoadMore: false, pageToken: null };
  }
  if (typeof nextPageToken === 'string' && nextPageToken) {
    return {
      items,
      status: 'ready',
      shouldLoadMore: true,
      pageToken: nextPageToken,
    };
  }
  return { items, status: 'empty', shouldLoadMore: false, pageToken: null };
}

/**
 * Whether Process / Reopen should be shown for a row under the active filter.
 */
export function responseActionVisibility(filter, processingState) {
  const state =
    processingState === 'processed' ? 'processed' : 'needs_processing';
  const active = filter === 'processed' ? 'processed' : 'needs_processing';
  return {
    showProcess: active === 'needs_processing' && state === 'needs_processing',
    showReopen: active === 'processed' && state === 'processed',
  };
}

/**
 * Coverage empty copy: incomplete/unknown must not present as definitive empty.
 */
export function responsesEmptyIsDefinitive(coverage) {
  return coverage === 'complete';
}

// --- Editor session ownership ------------------------------------------------

/**
 * @param {{ prospectId?: string|null, generation?: number }} [args]
 * @returns {{ prospectId: string, generation: number } | null}
 */
export function createEditorSession({ prospectId, generation } = {}) {
  const id = typeof prospectId === 'string' ? prospectId.trim() : '';
  if (!id) return null;
  const gen = Number(generation);
  return {
    prospectId: id,
    generation: Number.isFinite(gen) && gen > 0 ? gen : 0,
  };
}

/**
 * @param {number} [prev]
 * @returns {number}
 */
export function nextEditorGeneration(prev) {
  const n = Number(prev);
  return Number.isFinite(n) && n >= 0 ? n + 1 : 1;
}

/**
 * Session owns a refresh/detail apply only when prospectId AND generation match
 * and the page is still mounted. Null/closed session never owns.
 *
 * @param {{
 *   session: { prospectId: string, generation: number } | null | undefined,
 *   prospectId?: string|null,
 *   generation?: number|null,
 *   mounted?: boolean,
 * }} args
 */
export function isEditorSessionOwned({
  session,
  prospectId,
  generation,
  mounted = true,
} = {}) {
  if (!mounted) return false;
  if (!session || typeof session !== 'object') return false;
  const id = typeof prospectId === 'string' ? prospectId.trim() : '';
  if (!id || session.prospectId !== id) return false;
  if (generation == null) return false;
  return session.generation === generation;
}

/**
 * Compare prospect freshness via updatedAtVersion {s,n} or updatedAt.
 * @returns {'newer'|'older'|'same'|'unknown'} relative to `current`
 *   - newer: incoming is strictly newer than current
 *   - older: incoming is strictly older than current
 *   - same: equal versions
 *   - unknown: cannot compare (apply allowed)
 */
export function compareIncomingProspectFreshness(incoming, current) {
  if (!incoming || !current) return 'unknown';

  const inVer = normalizeVersionParts(incoming.updatedAtVersion);
  const curVer = normalizeVersionParts(current.updatedAtVersion);
  if (inVer && curVer) {
    if (inVer.s > curVer.s) return 'newer';
    if (inVer.s < curVer.s) return 'older';
    if (inVer.n > curVer.n) return 'newer';
    if (inVer.n < curVer.n) return 'older';
    return 'same';
  }

  const inAt = toMillis(incoming.updatedAt);
  const curAt = toMillis(current.updatedAt);
  if (inAt != null && curAt != null) {
    if (inAt > curAt) return 'newer';
    if (inAt < curAt) return 'older';
    return 'same';
  }

  return 'unknown';
}

function normalizeVersionParts(version) {
  if (!version || typeof version !== 'object') return null;
  const s = Number(version.s ?? version.seconds);
  const n = Number(version.n ?? version.nanoseconds ?? 0);
  if (!Number.isFinite(s)) return null;
  return { s, n: Number.isFinite(n) ? n : 0 };
}

function toMillis(value) {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === 'object') {
    if (typeof value.toMillis === 'function') {
      try {
        const ms = value.toMillis();
        return Number.isFinite(ms) ? ms : null;
      } catch {
        return null;
      }
    }
    const s = Number(value.seconds ?? value._seconds ?? value.s);
    const n = Number(value.nanoseconds ?? value._nanoseconds ?? value.n ?? 0);
    if (Number.isFinite(s)) return s * 1000 + Math.floor((Number.isFinite(n) ? n : 0) / 1e6);
  }
  return null;
}

/**
 * Apply an incoming prospect detail into the editor only when the session still owns
 * the open prospect. Never treats a null/cleared editor as permission to recreate.
 *
 * @returns {{ applied: boolean, prospect: object|null, reason?: string }}
 */
export function applyEditorProspectIfOwned({
  session,
  prospectId,
  generation,
  mounted = true,
  incoming,
  current,
} = {}) {
  if (
    !isEditorSessionOwned({
      session,
      prospectId,
      generation,
      mounted,
    })
  ) {
    return { applied: false, prospect: current ?? null, reason: 'not_owned' };
  }

  // Closed / cleared editor: do not reopen from a late refresh.
  if (!current || !current.id) {
    return { applied: false, prospect: null, reason: 'no_current' };
  }

  const ownerId = String(prospectId || '').trim();
  if (current.id !== ownerId) {
    return { applied: false, prospect: current, reason: 'id_mismatch' };
  }

  if (!incoming || typeof incoming !== 'object') {
    return { applied: false, prospect: current, reason: 'no_incoming' };
  }

  const freshness = compareIncomingProspectFreshness(incoming, current);
  if (freshness === 'older') {
    return { applied: false, prospect: current, reason: 'stale' };
  }

  // Keep id stable (editor key); merge server fields without remounting.
  return {
    applied: true,
    prospect: {
      ...current,
      ...incoming,
      id: current.id,
    },
  };
}

// --- Silent list load settlement ---------------------------------------------

/**
 * Status to show while a silent (background) refresh is in flight.
 */
export function silentRefreshBusyStatus(previousStatus) {
  return previousStatus === 'loading' ? 'loading' : 'refreshing';
}

/**
 * Settle a silent list load failure: keep rows, leave refreshing, expose error.
 *
 * @param {{
 *   previousStatus?: string,
 *   itemCount?: number,
 *   errorKind?: string|null,
 * }} args
 * @returns {{
 *   ok: false,
 *   applied: false,
 *   status: string,
 *   errorKind: string|null,
 *   abandoned: false,
 * }}
 */
export function settleSilentListFailure({
  previousStatus = 'ready',
  itemCount = 0,
  errorKind = null,
} = {}) {
  let status;
  if (itemCount > 0) {
    status = 'ready';
  } else if (previousStatus === 'empty') {
    status = 'empty';
  } else if (previousStatus === 'loading') {
    // Initial load still in loading — fall through to hard error semantics
    // only when caller uses non-silent path; for silent keep ready + banner.
    status = 'ready';
  } else if (
    previousStatus === 'ready' ||
    previousStatus === 'refreshing' ||
    previousStatus === 'paging'
  ) {
    status = 'ready';
  } else {
    status = 'ready';
  }

  return {
    ok: false,
    applied: false,
    status,
    errorKind: errorKind || null,
    abandoned: false,
  };
}

/**
 * When a list request is abandoned but still owns the request slot (e.g. process
 * bumped the responses epoch), settle any busy status it set so UI does not stick
 * on refreshing/loading/paging.
 *
 * @param {{
 *   ownsRequestSlot?: boolean,
 *   mounted?: boolean,
 *   previousStatus?: string,
 *   itemCount?: number,
 *   silent?: boolean,
 * }} args
 * @returns {{ shouldSettle: boolean, status: string|null }}
 */
export function settleAbandonedListBusy({
  ownsRequestSlot = false,
  mounted = true,
  previousStatus = 'ready',
  itemCount = 0,
  silent = false,
} = {}) {
  if (!mounted || !ownsRequestSlot) {
    return { shouldSettle: false, status: null };
  }
  let status;
  if (itemCount > 0) {
    status = 'ready';
  } else if (previousStatus === 'empty') {
    status = 'empty';
  } else if (silent) {
    status = previousStatus === 'loading' ? 'empty' : 'ready';
  } else if (previousStatus === 'loading' || previousStatus === 'paging') {
    status = 'empty';
  } else {
    status = previousStatus === 'refreshing' ? 'ready' : previousStatus;
  }
  return { shouldSettle: true, status };
}

/**
 * Build a structured list-load outcome for success / failure / abandon.
 *
 * @returns {{
 *   ok: boolean,
 *   applied: boolean,
 *   status: string,
 *   errorKind: string|null,
 *   items?: Array,
 *   nextPageToken?: string|null,
 *   abandoned?: boolean,
 * }}
 */
export function settleListLoadOutcome({
  abandoned = false,
  ok = false,
  silent = false,
  append = false,
  items = [],
  nextPageToken = null,
  errorKind = null,
  previousStatus = 'ready',
  itemCount = 0,
} = {}) {
  if (abandoned) {
    return {
      ok: false,
      applied: false,
      status: previousStatus,
      errorKind: null,
      abandoned: true,
    };
  }

  if (!ok) {
    if (silent) {
      return settleSilentListFailure({
        previousStatus,
        itemCount,
        errorKind,
      });
    }
    return {
      ok: false,
      applied: false,
      status: 'error',
      errorKind: errorKind || null,
      abandoned: false,
    };
  }

  const list = Array.isArray(items) ? items : [];
  const status = list.length === 0 && !append ? 'empty' : 'ready';
  return {
    ok: true,
    applied: true,
    status,
    errorKind: null,
    items: list,
    nextPageToken: nextPageToken || null,
    abandoned: false,
  };
}

/**
 * Show list error banner whenever an error is set and we are past the initial
 * blank loading state (including ready/refreshing-settled-with-rows).
 */
export function shouldShowListErrorBanner(status, error) {
  return Boolean(error) && status !== 'loading';
}

/**
 * Keep load-more when rows + next token exist (even after a settled refresh error).
 */
export function shouldShowLoadMore({ status, nextPageToken, itemCount = 0 } = {}) {
  if (!nextPageToken || itemCount <= 0) return false;
  if (status === 'loading' || status === 'paging') return false;
  return true;
}

// --- Responses epoch (process vs list race) ----------------------------------

/**
 * @param {number} [prev]
 * @returns {number}
 */
export function nextResponsesEpoch(prev) {
  const n = Number(prev);
  return Number.isFinite(n) && n >= 0 ? n + 1 : 1;
}

/**
 * Whether a responses list read may apply its result.
 */
export function shouldApplyResponsesLoad({
  epochAtStart,
  currentEpoch,
  mounted = true,
  filterAtStart,
  currentFilter,
} = {}) {
  if (!mounted) return false;
  if (epochAtStart !== currentEpoch) return false;
  if (
    filterAtStart != null &&
    currentFilter != null &&
    filterAtStart !== currentFilter
  ) {
    return false;
  }
  return true;
}

// --- Editor detail refresh (production orchestration) ------------------------

/**
 * Normalize a getAdminProspect payload into the workspace editor prospect shape.
 */
export function toEditorProspect(detail) {
  if (!detail || typeof detail !== 'object') return null;
  const id = detail.id || detail.prospectId;
  if (!id) return null;
  return {
    id,
    address: detail.address ?? null,
    city: detail.city ?? null,
    province: detail.province ?? null,
    postalCode: detail.postalCode ?? null,
    numberOfUnits: detail.numberOfUnits ?? null,
    ownerName: detail.ownerName ?? null,
    status: detail.status ?? null,
    source: detail.source ?? null,
    lastContactAt: detail.lastContactAt ?? null,
    nextFollowUpAt: detail.nextFollowUpAt ?? null,
    createdAt: detail.createdAt ?? null,
    updatedAt: detail.updatedAt ?? null,
    updatedAtVersion: detail.updatedAtVersion ?? null,
    followUpBucket: detail.followUpBucket ?? null,
  };
}

/**
 * Production post-save / conflict / retry refresh for the workspace editor.
 * Return value is always consistent with the accepted editor snapshot — never a
 * rejected stale detail payload.
 *
 * @param {{
 *   prospectId?: string|null,
 *   conflictReload?: boolean,
 *   getSession: () => { prospectId: string, generation: number } | null,
 *   getMounted: () => boolean,
 *   getCurrentEditorProspect: () => object|null,
 *   onEditorProspectAccepted?: (prospect: object) => void,
 *   loadDetail: (prospectId: string) => Promise<object|null>,
 *   loadFollowUps: () => Promise<{ ok?: boolean }>,
 *   loadOpportunities: () => Promise<{ ok?: boolean }>,
 * }} args
 */
export async function runWorkspaceEditorRefresh({
  prospectId,
  conflictReload = false,
  getSession,
  getMounted,
  getCurrentEditorProspect,
  onEditorProspectAccepted,
  loadDetail,
  loadFollowUps,
  loadOpportunities,
} = {}) {
  const ownerId = String(prospectId || '').trim();
  if (!ownerId) {
    return {
      prospectId: null,
      prospect: null,
      refreshOk: false,
      applied: false,
      detailOk: false,
      detailAccepted: false,
    };
  }

  const sessionAtStart = typeof getSession === 'function' ? getSession() : null;
  const generationAtStart = sessionAtStart?.generation;
  const stillOwned = () =>
    isEditorSessionOwned({
      session: typeof getSession === 'function' ? getSession() : null,
      prospectId: ownerId,
      generation: generationAtStart,
      mounted: typeof getMounted === 'function' ? getMounted() : true,
    });

  if (!stillOwned()) {
    return {
      prospectId: ownerId,
      prospect: null,
      refreshOk: false,
      applied: false,
      detailOk: false,
      detailAccepted: false,
    };
  }

  let incoming = null;
  let detailOk = false;
  try {
    incoming = await loadDetail(ownerId);
    detailOk = Boolean(incoming);
  } catch {
    detailOk = false;
    incoming = null;
  }

  if (!stillOwned()) {
    return {
      prospectId: ownerId,
      prospect: null,
      refreshOk: false,
      applied: false,
      detailOk,
      detailAccepted: false,
    };
  }

  const current = typeof getCurrentEditorProspect === 'function' ? getCurrentEditorProspect() : null;
  let detailAccepted = false;
  let detailRejectedReason = null;
  let acceptedProspect = current;

  if (incoming) {
    const applyResult = applyEditorProspectIfOwned({
      session: typeof getSession === 'function' ? getSession() : null,
      prospectId: ownerId,
      generation: generationAtStart,
      mounted: typeof getMounted === 'function' ? getMounted() : true,
      incoming,
      current,
    });
    if (applyResult.applied) {
      detailAccepted = true;
      acceptedProspect = applyResult.prospect;
      if (typeof onEditorProspectAccepted === 'function') {
        onEditorProspectAccepted(applyResult.prospect);
      }
    } else {
      detailRejectedReason = applyResult.reason || 'rejected';
      acceptedProspect = applyResult.prospect;
      // Never hand the rejected incoming snapshot to consumers.
    }
  }

  const [followUpsOutcome, oppsOutcome] = await Promise.all([
    typeof loadFollowUps === 'function' ? loadFollowUps() : Promise.resolve({ ok: true }),
    typeof loadOpportunities === 'function'
      ? loadOpportunities()
      : Promise.resolve({ ok: true }),
  ]);

  if (!stillOwned()) {
    return {
      prospectId: ownerId,
      prospect: null,
      refreshOk: false,
      applied: true,
      detailOk,
      detailAccepted: false,
      detailRejectedReason: detailRejectedReason || 'not_owned',
    };
  }

  const followUpsOk = Boolean(followUpsOutcome?.ok);
  const oppsOk = Boolean(oppsOutcome?.ok);
  const listsOk = followUpsOk && oppsOk;

  // Revalidate after list awaits: another refresh may have accepted a newer detail.
  const latestCurrent =
    typeof getCurrentEditorProspect === 'function' ? getCurrentEditorProspect() : acceptedProspect;

  let deliveryAccepted = detailAccepted;
  let deliveryProspect = acceptedProspect;
  let deliveryRejectedReason = detailRejectedReason;
  let superseded = false;

  if (detailAccepted && acceptedProspect && latestCurrent) {
    const freshnessVsLatest = compareIncomingProspectFreshness(acceptedProspect, latestCurrent);
    if (freshnessVsLatest === 'older') {
      superseded = true;
      deliveryAccepted = false;
      deliveryRejectedReason = 'superseded';
      deliveryProspect = latestCurrent;
    } else if (freshnessVsLatest === 'same' || freshnessVsLatest === 'newer' || freshnessVsLatest === 'unknown') {
      // Prefer the authoritative live editor snapshot at delivery time.
      deliveryProspect = latestCurrent || acceptedProspect;
    }
  } else if (!detailAccepted) {
    deliveryProspect = latestCurrent || acceptedProspect;
  }

  if (conflictReload) {
    const refreshOk = deliveryAccepted && listsOk && !superseded;
    return {
      prospectId: ownerId,
      // Only deliver when our accept is still authoritative at delivery time.
      prospect: deliveryAccepted ? deliveryProspect : null,
      refreshOk,
      applied: true,
      detailOk,
      detailAccepted: deliveryAccepted,
      detailRejectedReason: deliveryRejectedReason,
      superseded,
      authoritativeProspect: latestCurrent || null,
      followUpsOk,
      oppsOk,
    };
  }

  const refreshOk = listsOk && (superseded ? Boolean(latestCurrent) : detailOk);
  return {
    prospectId: ownerId,
    prospect: superseded ? latestCurrent || null : deliveryProspect || latestCurrent || null,
    refreshOk,
    applied: true,
    detailOk,
    detailAccepted: deliveryAccepted,
    detailRejectedReason: deliveryRejectedReason,
    superseded,
    authoritativeProspect: latestCurrent || null,
    followUpsOk,
    oppsOk,
  };
}

/**
 * Whether a refresh/conflict consumer may treat `incoming` as authoritative
 * relative to the form's current baseline / editor prospect.
 */
export function shouldAcceptRefreshProspectForConsumer({
  incoming,
  referenceProspect,
  requireAccepted = true,
  detailAccepted = true,
} = {}) {
  if (requireAccepted && detailAccepted === false) return false;
  if (!incoming || typeof incoming !== 'object') return false;
  if (!referenceProspect) return true;
  const freshness = compareIncomingProspectFreshness(incoming, referenceProspect);
  return freshness !== 'older';
}

/**
 * Apply a conflict-reload refresh into form state without regressing to a stale
 * snapshot. On failure, keep drafts/conflict and surface conflict-reload retry
 * (not post-write "saved" messaging).
 *
 * Prefer `referenceProspect` from the authoritative editor (parent prop / live
 * snapshot), not only a dirty form's potentially stale baseline token.
 */
export function applyWorkspaceConflictReloadToForm({
  formState,
  control,
  refresh,
  referenceProspect = null,
} = {}) {
  if (!formState || !control) {
    return { ok: false, formState, reason: 'invalid' };
  }

  const reference =
    referenceProspect ||
    refresh?.authoritativeProspect ||
    (formState.baselines?.versionToken
      ? {
          id: formState.prospectId,
          updatedAtVersion: formState.baselines.versionToken,
        }
      : null);

  if (refresh?.superseded || refresh?.detailRejectedReason === 'superseded') {
    return {
      ok: false,
      reason: 'superseded',
      formState: markQuickFollowUpConflictReloadFailure(formState, control),
    };
  }

  if (!refresh?.detailAccepted || !refresh?.prospect) {
    return {
      ok: false,
      reason: refresh?.detailRejectedReason || 'reload_failed',
      formState: markQuickFollowUpConflictReloadFailure(formState, control),
    };
  }

  if (
    !shouldAcceptRefreshProspectForConsumer({
      incoming: refresh.prospect,
      referenceProspect: reference,
      requireAccepted: true,
      detailAccepted: true,
    })
  ) {
    return {
      ok: false,
      reason: 'stale',
      formState: markQuickFollowUpConflictReloadFailure(formState, control),
    };
  }

  let next = enterQuickFollowUpConflictReview(
    syncQuickFollowUpFormFromProspect(formState, refresh.prospect),
    control,
    refresh.prospect,
  );
  // Detail accepted but lists failed — keep conflict values, still offer read-only retry.
  if (!refresh.refreshOk) {
    next = markQuickFollowUpConflictReloadFailure(next, control);
  } else {
    next = clearQuickFollowUpRefreshFailure(next);
  }

  return { ok: true, formState: next };
}

/**
 * Apply a completed read-only retry outcome to the *current* form state.
 * Never installs a form snapshot captured before the await.
 */
export function applyWorkspaceEditorRefreshRetryOutcome({
  formState,
  control,
  reason = 'post_write',
  refresh = null,
  authoritativeProspect = null,
} = {}) {
  if (!formState || !control) {
    return { ok: false, formState, reason: 'invalid' };
  }

  const recoveryReason = reason === 'conflict_reload' ? 'conflict_reload' : 'post_write';
  const liveWarning = formState.refreshAfterWrite;
  const ownsWarning =
    liveWarning?.control === control &&
    (liveWarning?.reason || 'post_write') === recoveryReason;

  if (recoveryReason === 'conflict_reload') {
    return applyWorkspaceConflictReloadToForm({
      formState,
      control,
      refresh,
      referenceProspect: authoritativeProspect,
    });
  }

  if (refresh?.refreshOk === false && refresh?.applied) {
    return {
      ok: false,
      formState: markQuickFollowUpRefreshFailure(formState, control),
      reason: 'post_write',
    };
  }

  if (refresh?.refreshOk) {
    // Clear only the warning owned by this completed recovery.
    if (!ownsWarning) {
      return { ok: true, formState, reason: 'post_write' };
    }
    return {
      ok: true,
      formState: clearQuickFollowUpRefreshFailure(formState),
      reason: 'post_write',
    };
  }

  return { ok: false, formState, reason: 'post_write' };
}

/**
 * Production read-only retry for workspace QuickActions.
 * Captures only recovery identity (control/reason) before await, then applies
 * the outcome to the latest form via `applyToForm` (functional updater).
 * Never submits drafts or replays writes.
 */
export async function runWorkspaceEditorRefreshRetry({
  formState,
  getAuthoritativeProspect = null,
  authoritativeProspect = null,
  applyToForm = null,
  onRefresh,
  mayApply = () => true,
  requestVisitId = null,
} = {}) {
  const control = formState?.refreshAfterWrite?.control;
  const reason = formState?.refreshAfterWrite?.reason || 'post_write';
  if (!control || typeof onRefresh !== 'function') {
    return { ok: false, abandoned: false, formState, reason: 'missing' };
  }

  const refresh =
    reason === 'conflict_reload'
      ? await onRefresh({ conflictReload: true })
      : await onRefresh({ refreshOnly: true, conflictReload: false });

  if (!mayApply(requestVisitId)) {
    return { ok: false, abandoned: true, formState, reason: 'abandoned' };
  }

  const authoritative =
    typeof getAuthoritativeProspect === 'function'
      ? getAuthoritativeProspect()
      : authoritativeProspect;

  let outcome = null;
  if (typeof applyToForm === 'function') {
    applyToForm((current) => {
      outcome = applyWorkspaceEditorRefreshRetryOutcome({
        formState: current,
        control,
        reason,
        refresh,
        authoritativeProspect: authoritative,
      });
      return outcome.formState;
    });
  } else {
    outcome = applyWorkspaceEditorRefreshRetryOutcome({
      formState,
      control,
      reason,
      refresh,
      authoritativeProspect: authoritative,
    });
  }

  return {
    ok: Boolean(outcome?.ok),
    abandoned: false,
    formState: outcome?.formState ?? formState,
    reason: outcome?.reason || reason,
    refresh,
  };
}
